import axios, {
  type AxiosInstance,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from "axios";
import { msalInstance } from "./msal/msalInstance";
import {
  forceLogoutAndRedirectToLogin,
  getSupersetAuthToken,
  getToken,
} from "./auth/authService";
import { ForbiddenPath, UnauthorizedPath } from "../constants";
import { trimRequestPayload } from "../utils/requestPayload.util";
import { isCanceledError } from "../utils/cancellation.util";

/* ------------------ DUPLICATE REQUEST CANCELLATION ------------------ */
// Tracks in-flight requests keyed by "METHOD url" so an identical request
// in-flight gets aborted when a newer one is issued.
const pendingRequests = new Map<string, AbortController>();

const getRequestKey = (config: InternalAxiosRequestConfig): string => {
  const method = (config.method ?? "get").toUpperCase();
  const url = `${config.baseURL ?? ""}${config.url ?? ""}`;
  const body =
    typeof config.data === "string"
      ? config.data
      : JSON.stringify(config.data ?? "");
  const params =
    typeof config.params === "string"
      ? config.params
      : JSON.stringify(config.params ?? "");

  return `${method} ${url} ${body} ${params}`;
};

const shouldDedupeCancel = (config?: InternalAxiosRequestConfig): boolean => {
  if (!config) {
    return false;
  }
  return config.skipDedupeCancellation !== true;
};

const cancelDuplicateRequest = (config: InternalAxiosRequestConfig): void => {
  if (!shouldDedupeCancel(config) || config.signal) {
    return;
  }

  const key = getRequestKey(config);
  pendingRequests.get(key)?.abort();

  const controller = new AbortController();
  config.signal = controller.signal;
  config._dedupeRequestKey = key;
  pendingRequests.set(key, controller);
};

const clearPendingRequest = (config?: InternalAxiosRequestConfig): void => {
  if (!config || !shouldDedupeCancel(config)) {
    return;
  }

  // Use the key captured at insertion time; config.data/params may have since
  // been trimmed or had markers stripped, which would change a recomputed key.
  const key = config._dedupeRequestKey;
  if (!key) {
    return;
  }

  if (pendingRequests.get(key)?.signal === config.signal) {
    pendingRequests.delete(key);
  }
};

// Aborts every tracked in-flight request (e.g. call on logout) and clears the tracker.
export const abortAllPendingRequests = (): void => {
  pendingRequests.forEach((controller) => controller.abort());
  pendingRequests.clear();
};

const removeSupersetMarkers = (config: InternalAxiosRequestConfig): void => {
  if (config.data && typeof config.data === "object") {
    const requestData = config.data as Record<string, unknown>;
    delete requestData.isSuperset;
    delete requestData.isSupersetRefresh;
  }

  if (config.params && typeof config.params === "object") {
    const requestParams = config.params as Record<string, unknown>;
    delete requestParams.isSuperset;
    delete requestParams.isSupersetRefresh;
  }
};

/* ------------------ AXIOS INSTANCE ------------------ */

const axiosInstance: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

/* ------------------ REQUEST INTERCEPTOR ------------------ */

axiosInstance.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    cancelDuplicateRequest(config);
    try {
      const token = await getToken();
      const account = msalInstance.getActiveAccount();

      const isSuperset =
        (config.data && config.data.isSuperset === true) ||
        (config.params && config.params.isSuperset === true);
      const isSupersetRefresh =
        (config.data && config.data.isSupersetRefresh === true) ||
        (config.params && config.params.isSupersetRefresh === true);

      config.data = trimRequestPayload(config.data);
      config.params = trimRequestPayload(config.params);

      if (token && account?.idTokenClaims?.sub) {
        config.headers.Authorization = `Bearer ${token}`;
        config.headers["X-User"] = window.btoa(account.idTokenClaims.sub);
        config.headers["x-Use-Page-Permissions"] = true;

        if (isSuperset || isSupersetRefresh) {
          const supersetAuthToken =
            await getSupersetAuthToken(isSupersetRefresh);
          if (supersetAuthToken) {
            config.headers["X-Superset-Auth-Token"] = supersetAuthToken;
            removeSupersetMarkers(config);
          }
        }
      }
    } catch (err) {
      // A canceled/aborted request is not an auth failure; let it propagate so
      // downstream interceptors (loader onStop) still run for it.
      if (isCanceledError(err)) {
        throw err;
      }
      abortAllPendingRequests();
      await forceLogoutAndRedirectToLogin();
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

/* ------------------ RESPONSE INTERCEPTOR ------------------ */

axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => {
    clearPendingRequest(response.config);
    return response;
  },
  async (error) => {
    clearPendingRequest(error?.config);
    if (isCanceledError(error)) {
      // Pass through unchanged; setupAxiosLoader's onStop still needs to run for it.
      return Promise.reject(error);
    }

    const status = error?.response?.status;

    if (status === 401) {
      window.location.href = UnauthorizedPath;
    }

    if (status === 403) {
      window.location.href = ForbiddenPath;
    }

    const responseData = error?.response?.data;
    if (responseData) {
      return Promise.reject(responseData);
    }

    return Promise.reject(error?.message || "Request failed");
  },
);

export default axiosInstance;
